import React, { useState } from 'react'
import { useLanguage } from '../../hooks/useLanguage'
import { ComplaintFormData, ComplaintCategory } from '../../types'
import { getCurrentLocation } from '../../utils/location'
import { validateImageFile, compressImage } from '../../utils/fileUpload'
import { 
  CameraIcon, 
  LocationIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CloseIcon,
  RoadIcon,
  GarbageIcon,
  WaterIcon,
  StreetlightIcon,
  ElectricityIcon
} from '../icons/Icons'

interface StepwiseComplaintFormProps {
  onSubmit: (data: ComplaintFormData) => Promise<void>
  loading?: boolean
  onClose?: () => void
}

const CATEGORIES = [
  { value: 'road' as ComplaintCategory, label: 'Road Issues', icon: RoadIcon, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  { value: 'garbage' as ComplaintCategory, label: 'Garbage Collection', icon: GarbageIcon, color: 'text-green-600', bgColor: 'bg-green-50' },
  { value: 'water' as ComplaintCategory, label: 'Water Supply', icon: WaterIcon, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { value: 'streetlight' as ComplaintCategory, label: 'Street Light', icon: StreetlightIcon, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  { value: 'drainage' as ComplaintCategory, label: 'Drainage', icon: WaterIcon, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  { value: 'electricity' as ComplaintCategory, label: 'Electricity', icon: ElectricityIcon, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { value: 'public_transport' as ComplaintCategory, label: 'Public Transport', icon: LocationIcon, color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
  { value: 'noise' as ComplaintCategory, label: 'Noise Pollution', icon: LocationIcon, color: 'text-red-600', bgColor: 'bg-red-50' },
  { value: 'other' as ComplaintCategory, label: 'Other', icon: LocationIcon, color: 'text-gray-600', bgColor: 'bg-gray-50' }
]

const StepwiseComplaintForm: React.FC<StepwiseComplaintFormProps> = ({ 
  onSubmit, 
  loading = false, 
  onClose 
}) => {
  const { t } = useLanguage()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<ComplaintFormData>({
    title: '',
    description: '',
    category: 'other',
    location: { latitude: 0, longitude: 0 }
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [manualLocation, setManualLocation] = useState('')
  const [useManualLocation, setUseManualLocation] = useState(false)

  const steps = [
    { number: 1, title: t('stepTitle'), description: 'Basic information about the issue' },
    { number: 2, title: t('stepLocation'), description: 'Where is the problem located?' },
    { number: 3, title: t('stepDescription'), description: 'Detailed description and photos' },
    { number: 4, title: t('stepReview'), description: 'Review and submit your complaint' }
  ]

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleGetLocation = async () => {
    setLocationLoading(true)
    setLocationError('')

    try {
      const location = await getCurrentLocation()
      setFormData(prev => ({ ...prev, location }))
      setUseManualLocation(false)
    } catch (error: any) {
      setLocationError(error.message || 'Failed to get location')
    } finally {
      setLocationLoading(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      await validateImageFile(file)
      const compressedFile = await compressImage(file)
      setImageFile(compressedFile)
      
      const reader = new FileReader()
      reader.onload = () => setImagePreview(reader.result as string)
      reader.readAsDataURL(compressedFile)
    } catch (error: any) {
      alert(error.message)
    }
  }

  const handleSubmit = async () => {
    try {
      const finalData: ComplaintFormData = {
        ...formData,
        image: imageFile || undefined,
        location: useManualLocation 
          ? { ...formData.location, address: manualLocation }
          : formData.location
      }
      await onSubmit(finalData)
    } catch (error) {
      console.error('Error submitting complaint:', error)
    }
  }

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.title.trim() !== '' && formData.category !== undefined
      case 2:
        return (formData.location.latitude !== 0 && formData.location.longitude !== 0) || 
               (useManualLocation && manualLocation.trim() !== '')
      case 3:
        return formData.description.trim() !== ''
      default:
        return true
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-blue-50">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('formTitle')}</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <CloseIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex items-center flex-shrink-0">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    currentStep >= step.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {currentStep > step.number ? (
                      <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="ml-2 sm:ml-3 hidden xs:block">
                    <p className={`text-xs sm:text-sm font-medium ${
                      currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500 hidden sm:block">{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 sm:mx-2 flex-shrink-0 ${
                    currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 160px)' }}>
          {/* Step 1: Title & Category */}
          {currentStep === 1 && (
            <div className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  {t('complaintTitle')} *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('titlePlaceholder')}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base sm:text-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 sm:mb-4">
                  {t('category')} *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {CATEGORIES.map((cat) => {
                    const IconComponent = cat.icon
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                        className={`p-3 sm:p-4 border-2 rounded-lg text-left transition-all ${
                          formData.category === cat.value
                            ? `border-current ${cat.color} ${cat.bgColor}`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`flex items-center space-x-2 sm:space-x-3 ${
                          formData.category === cat.value ? cat.color : 'text-gray-600'
                        }`}>
                          <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="font-medium text-xs sm:text-sm">{cat.label}</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="space-y-5 sm:space-y-6">
              <div className="text-center">
                <LocationIcon className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  {t('stepLocation')}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base px-2">
                  Help us locate the issue by providing the exact location
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={handleGetLocation}
                  disabled={locationLoading}
                  className="p-5 sm:p-6 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-400 transition-colors text-center"
                >
                  <LocationIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-2 sm:mb-3" />
                  <p className="font-medium text-gray-900 text-sm sm:text-base">
                    {locationLoading ? 'Getting Location...' : t('getLocation')}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Use GPS to get current location
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setUseManualLocation(true)}
                  className="p-5 sm:p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <span className="text-white text-xs sm:text-sm font-bold">A</span>
                  </div>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{t('manualLocation')}</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Enter address manually
                  </p>
                </button>
              </div>

              {useManualLocation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Location Address *
                  </label>
                  <input
                    type="text"
                    value={manualLocation}
                    onChange={(e) => setManualLocation(e.target.value)}
                    placeholder={t('locationPlaceholder')}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  />
                </div>
              )}

              {formData.location.latitude !== 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    <span className="text-green-800 font-medium text-sm sm:text-base">Location captured!</span>
                  </div>
                  <p className="text-green-700 text-xs sm:text-sm mt-1">
                    {formData.location.latitude.toFixed(6)}, {formData.location.longitude.toFixed(6)}
                  </p>
                </div>
              )}

              {locationError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                  <p className="text-red-800 text-xs sm:text-sm">{locationError}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Description & Photos */}
          {currentStep === 3 && (
            <div className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  {t('description')} *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('descriptionPlaceholder')}
                  rows={5}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 sm:mb-4">
                  {t('uploadPhoto')} (Optional)
                </label>
                
                {!imagePreview ? (
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-blue-400 transition-colors cursor-pointer">
                      <CameraIcon className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                      <p className="text-gray-600 font-medium text-sm sm:text-base">Click to take photo or upload image</p>
                      <p className="text-gray-500 text-xs sm:text-sm mt-1">JPG, PNG up to 5MB</p>
                    </div>
                  </label>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-32 sm:h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null)
                        setImagePreview(null)
                      }}
                      className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 p-1 sm:p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                    >
                      <CloseIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-5 sm:space-y-6">
              <div className="text-center mb-5 sm:mb-6">
                <CheckCircleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1.5 sm:mb-2">
                  Review Your Complaint
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Please review all information before submitting
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Title</label>
                  <p className="text-gray-900 font-medium text-sm sm:text-base">{formData.title}</p>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Category</label>
                  <p className="text-gray-900 font-medium capitalize text-sm sm:text-base">{formData.category}</p>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Location</label>
                  <p className="text-gray-900 text-sm sm:text-base">
                    {useManualLocation 
                      ? manualLocation 
                      : `${formData.location.latitude.toFixed(6)}, ${formData.location.longitude.toFixed(6)}`
                    }
                  </p>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-500">Description</label>
                  <p className="text-gray-900 text-sm sm:text-base">{formData.description}</p>
                </div>

                {imagePreview && (
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">Photo</label>
                    <img
                      src={imagePreview}
                      alt="Attached"
                      className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg mt-2"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-4 sm:px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            {t('previous')}
          </button>

          <div className="flex items-center space-x-2 sm:space-x-3">
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
                className="px-5 sm:px-8 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1.5 sm:space-x-2 text-sm sm:text-base"
              >
                <span>{t('next')}</span>
                <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !isStepValid(currentStep)}
                className="px-5 sm:px-8 py-2.5 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
              >
                {loading ? t('submitting') : t('submit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StepwiseComplaintForm